import { IconInformationFill, IconQuestionOutline, IconSuccessFill } from '@saucelabs/ds-icons';
import { modalActions } from '@saucelabs/sl-container-modal';
import React from 'react';
import { useDispatch } from 'react-redux';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Typography } from '@saucelabs/ds-typography';
import { Link, LinkButton } from '@saucelabs/ds-link';

import { CREDITS_AUTO_TOPUP_MODAL } from '../../constants';
import { CreditsTopupModal } from './CreditsTopupModal';

const CREDITS_TOPUP_TOOLTIP = `
When you have fewer than 1,000 credits remaining within a billing cycle,
 25,000 credits will automatically be added to your account.
 You’ll be charged $50 plus taxes and fees. Auto-refill credits expire after 32 days.
`;

interface CreditsTopupColumnProps {
  topupEnabled?: boolean
  updateAutoTopup: (p: boolean) => void
}

export const CreditsTopupColumn: React.FC<CreditsTopupColumnProps> = ({
  topupEnabled,
  updateAutoTopup,
}) => {
  const dispatch = useDispatch();
  const autoTopupStatusText = topupEnabled ? 'Auto-refill enabled' : 'Auto-refill disabled';
  const nagText = topupEnabled ? "You'll never run out of test capacity" : 'Enable it to never run out of test capacity';
  const autoTopupStatusIcon = topupEnabled ? (
    <IconSuccessFill className="fill-current text-green-600 mr-1 h-2 w-2" />
  ) : (
    <IconInformationFill className="fill-current text-gray-500 mr-1 h-2 w-2" />
  );

  const showModal = () => {
    dispatch(modalActions.showModal(CREDITS_AUTO_TOPUP_MODAL));
  };

  const disableTopup = () => {
    updateAutoTopup(false);
    dispatch(notificationActions.notify({
      persist: false,
      time: 5000,
      text: 'You can enable it at any time.',
      header: 'You have successfully disabled auto-refill',
      v2: true,
      category: 'success',
    }));
  };

  const enableTopup = () => {
    updateAutoTopup(true);
    dispatch(notificationActions.notify({
      persist: false,
      time: 5000,
      header: 'You have successfully enabled auto-refill',
      v2: true,
      category: 'success',
    }));
  };

  return (
    <>
      <div
        className="pl-3"
        data-qa="summary-tile-plan-auto-renew"
      >
        <Typography
          className="font-semibold text-high-emphasis flex flex-row items-center"
          component="span"
          variant="body-large"
        >
          Credits Refill
          <Tooltip
            placement="top"
            title={CREDITS_TOPUP_TOOLTIP}
          >
            <div className="flex flex-row items-center ml-1">
              <IconQuestionOutline
                className="fill-current text-gray-500 h-2"
              />
            </div>
          </Tooltip>
        </Typography>
        <Typography
          className="flex flex-row items-center mt-1"
          component="span"
          variant="body"
        >
          {autoTopupStatusIcon}
          {autoTopupStatusText}
        </Typography>
        <Typography
          className="text-medium-emphasis text-gray-600 mt-1"
          data-qa="summary-tile-next-bill-date"
          variant="body"
        >
          {nagText}
        </Typography>
        <div className="mt-2">
          <LinkButton
            emphasize
            onPress={showModal}
            variant="primary"
          >
            Manage Credits Refill
          </LinkButton>
        </div>
      </div>
      <CreditsTopupModal
        disableTopupCallback={disableTopup}
        enableTopupCallback={enableTopup}
        topupEnabled={topupEnabled}
      />
    </>
  );
};
